import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { isSamePointOnLineString } from '@racemap/utilities/functions/validation';
import {
  type EventTrackObject,
  type GeoElementObject,
  type GeoObjects,
  type LayerObject,
  type POIObject,
  RacemapGeoTypes,
  type SplitObject,
} from '@racemap/utilities/types/geos';
import { type Immutable, produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { Tristate } from '../../../components/utils/tristateTask';
import type { DraftState, State } from '../../reducers';
import type { EventsActions } from '../events_reducers';
import { type GetState, type SetState, setSavingState } from './helpers';

const apiClient = RacemapAPIClient.fromWindowLocation();

function hasFeatureOfEvent(
  features: Immutable<GeoObjects>,
  eventId: string | null,
): eventId is string {
  return eventId != null && features.some((f) => f.properties?.eventId === eventId);
}

function isDuplicatePointOnLineString(error: unknown): boolean {
  return (
    error instanceof HTTPFetchError &&
    typeof error.serverError === 'string' &&
    error.serverError.startsWith('There is already a point on')
  );
}

async function saveGeoElementUpdate(
  id: string,
  racemapType: RacemapGeoTypes | undefined,
  element: Partial<GeoElementObject> | Immutable<Partial<GeoElementObject>>,
): Promise<void> {
  switch (racemapType) {
    case RacemapGeoTypes.TRACK:
      await apiClient.updateGeoLineString(id, element as EventTrackObject);
      return;
    case RacemapGeoTypes.POI:
    case RacemapGeoTypes.SPLIT:
      await apiClient.updateGeoPoint(id, element as SplitObject | POIObject);
      return;
    case RacemapGeoTypes.EXTERNAL_LAYER:
      await apiClient.updateGeoLayer(id, element as LayerObject);
      return;
    default:
      throw new Error('Only can update geo elements of known types.');
  }
}

async function createGeoElementOfType(
  racemapType: RacemapGeoTypes | undefined,
  element: Partial<GeoElementObject> | Immutable<Partial<GeoElementObject>>,
): Promise<void> {
  switch (racemapType) {
    case RacemapGeoTypes.TRACK:
      await apiClient.createGeoLineString(element as EventTrackObject);
      return;
    case RacemapGeoTypes.POI:
    case RacemapGeoTypes.SPLIT:
      await apiClient.createGeoPoint(element as SplitObject | POIObject);
      return;
    case RacemapGeoTypes.EXTERNAL_LAYER:
      await apiClient.createGeoLayer(element as LayerObject);
      return;
    default:
      throw new Error('Only can update geo elements of known types.');
  }
}

async function removeGeoElementOfType(
  id: string,
  racemapType: RacemapGeoTypes | undefined,
): Promise<void> {
  switch (racemapType) {
    case RacemapGeoTypes.TRACK:
      await apiClient.removeGeoLineString(id);
      return;
    case RacemapGeoTypes.POI:
    case RacemapGeoTypes.SPLIT:
      await apiClient.removeGeoPoint(id);
      return;
    case RacemapGeoTypes.EXTERNAL_LAYER:
      await apiClient.removeGeoLayer(id);
      return;
    default:
      throw new Error('Only can remove geo elements of known types.');
  }
}

async function saveBatchElement(get: GetState, feature: Immutable<GeoElementObject>) {
  const options = { reload: false };
  try {
    const { id, properties } = feature;
    const { eventId } = properties || {};
    if (id == null || properties == null || eventId == null)
      throw new Error('Only can update geo elements with an id and properties!');

    const event = get().events.items.get(eventId);
    const existingElement = event?.geo.features?.find((f) => f.id === id);
    const existingPointOnLineString = event?.geo.features?.find((f) =>
      isSamePointOnLineString(f, feature),
    );

    if (existingElement != null) {
      await get().events.actions.updateGeoElement(feature, options);
    } else if (existingPointOnLineString != null) {
      await get().events.actions.updateGeoElement(
        {
          ...existingPointOnLineString,
          properties: {
            ...existingPointOnLineString.properties,
            ...feature.properties,
          },
        },
        options,
      );
    } else {
      // reload so the file's splits merge into the server's default splits of a new track
      await get().events.actions.createGeoElement(feature, {
        reload: properties.racemapType === RacemapGeoTypes.TRACK,
      });
    }
  } catch (error) {
    if (isDuplicatePointOnLineString(error)) {
      console.warn('Skipping duplicated point on linestring');
    } else {
      console.error(error);
      toast.error(`Failed to update the geo element ${feature.id}`);
    }
  }
}

const askBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();

// Batches can overlap (an import and a split list save), so count them.
let activeGeoSaves = 0;

const setSavingGeoElements = (set: SetState, delta: 1 | -1) => {
  activeGeoSaves = Math.max(0, activeGeoSaves + delta);
  const isSaving = activeGeoSaves > 0;
  if (isSaving) window.addEventListener('beforeunload', askBeforeLeaving);
  else window.removeEventListener('beforeunload', askBeforeLeaving);
  set(
    produce((s: DraftState) => {
      s.events.isSavingGeoElements = isSaving;
    }),
  );
};

const setFeaturesOfEvent = (set: SetState, eventId: string, features: Immutable<GeoObjects>) =>
  set(
    produce((s) => {
      const event = s.events.items.get(eventId);
      event.geo.features = features;
    }),
  );

// Drops the matching features from the current event at once. `rollBack` restores them.
function removeFeaturesOptimistically(
  set: SetState,
  get: GetState,
  eventId: string,
  isRemoved: (feature: Immutable<GeoElementObject>) => boolean,
) {
  const isCurrentEvent = eventId === get().events.currentEventId;
  const features = get().events.items.get(eventId)?.geo?.features || [];

  if (isCurrentEvent) {
    setFeaturesOfEvent(
      set,
      eventId,
      features.filter((f) => !isRemoved(f)),
    );
  }

  return {
    isCurrentEvent,
    rollBack: () => {
      if (isCurrentEvent) setFeaturesOfEvent(set, eventId, features);
    },
  };
}

export const createGeoElementActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  EventsActions,
  | 'reorderGeoElements'
  | 'updateGeoElements'
  | 'updateGeoElement'
  | 'createGeoElement'
  | 'deleteGeoElement'
  | 'deleteGeoElements'
  | 'changeShadowtrack'
> => ({
  reorderGeoElements: async (element, targetIndex) => {
    const eventId = element.properties?.eventId;
    if (eventId == null) return;

    // The backend owns re-sequencing: it pulls this element out and re-inserts it at the
    // target slot, then normalizes the whole set to a contiguous 0..n-1. We send a single
    // PATCH for the moved element and mirror that reordering locally for an optimistic update.
    const racemapType = element.properties.racemapType;
    const features = get().events.items.get(eventId)?.geo?.features;
    if (!Array.isArray(features)) return;

    const siblings = features
      .filter((f) => f.properties?.racemapType === racemapType)
      .sort(
        (a, b) =>
          ((a.properties as { index?: number }).index ?? Number.POSITIVE_INFINITY) -
          ((b.properties as { index?: number }).index ?? Number.POSITIVE_INFINITY),
      );

    const fromIndex = siblings.findIndex((f) => f.id === element.id);
    if (fromIndex === -1) return;

    const reordered = [...siblings];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(Math.max(0, Math.min(targetIndex, reordered.length)), 0, moved);

    // Remember previous order values so we can roll back if persistence fails.
    const previousIndexes = siblings.map((f) => ({
      id: f.id,
      index: (f.properties as { index?: number }).index,
    }));
    const nextIndexes = reordered.map((f, index) => ({ id: f.id, index }));

    const applyIndexes = (s: DraftState, indexes: Array<{ id: string; index?: number }>) => {
      const features = s.events.items.get(eventId)?.geo.features;
      if (!Array.isArray(features)) return;
      for (const { id, index } of indexes) {
        const feature = features.find((f) => f.id === id);
        if (feature != null) (feature.properties as { index?: number }).index = index;
      }
    };

    // Optimistic update: write the new order indices into the store immediately.
    set(
      produce((s: DraftState) => {
        s.events.savingState = Tristate.SPINNING;
        applyIndexes(s, nextIndexes);
      }),
    );

    try {
      const updated = {
        ...element,
        properties: { ...element.properties, index: targetIndex },
      };
      if (racemapType === RacemapGeoTypes.TRACK) {
        await apiClient.updateGeoLineString(element.id, updated as EventTrackObject);
      } else {
        await apiClient.updateGeoPoint(element.id, updated as POIObject);
      }

      if (eventId === get().events.currentEventId) {
        await get().events.actions.loadCurrentEvent(eventId);
      }
      setSavingState(set, Tristate.NORMAL);
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.events.savingState = Tristate.NORMAL;
          applyIndexes(s, previousIndexes);
        }),
      );
      console.error(`Failed to reorder geo elements: ${error}`);
      toast.error('The attempt to reorder the elements failed.');
    }
  },
  updateGeoElements: async (newFeatures, { onProgress } = {}) => {
    setSavingGeoElements(set, 1);
    try {
      for (const [index, feature] of newFeatures.entries()) {
        await saveBatchElement(get, feature);
        onProgress?.(index + 1, newFeatures.length);
      }

      const currentEventId = get().events.currentEventId;
      if (hasFeatureOfEvent(newFeatures, currentEventId)) {
        await get().events.actions.loadCurrentEvent(currentEventId);
      }
    } finally {
      setSavingGeoElements(set, -1);
    }
  },
  updateGeoElement: async (newElement, { reload = true } = {}) => {
    const { id, properties } = newElement;
    const { eventId } = properties || {};
    const isCurrentEvent = eventId === get().events.currentEventId;

    if (isCurrentEvent) {
      set(
        produce((s: DraftState) => {
          s.events.savingState = Tristate.SPINNING;

          const event = s.events.items.get(eventId);
          const features = event?.geo.features;
          const featureIndex = features?.findIndex((f) => f.id === id);

          if (Array.isArray(features) && featureIndex != null && featureIndex > -1) {
            Object.assign(features[featureIndex], newElement);
          }
        }),
      );
    }

    try {
      if (id == null || properties == null || eventId == null)
        throw new Error('Only can update geo elements with an id and properties!');

      await saveGeoElementUpdate(id, properties.racemapType, newElement);

      if (isCurrentEvent && reload) {
        await get().events.actions.loadCurrentEvent(eventId);
      }
      setSavingState(set, Tristate.NORMAL);
    } catch (error) {
      if (error instanceof Error) {
        toast.error('Failed to update a geo element: ');
        console.error('Failed to update a geo element: ', error.message);

        if (error instanceof HTTPFetchError) {
          console.error(error.serverError);
        } else {
          console.error(error);
        }
      }

      if (isCurrentEvent) {
        setSavingState(set, Tristate.FAILURE);
        await get().events.actions.loadCurrentEvent(eventId);
      }
    }
  },
  createGeoElement: async (newElement, { reload = true } = {}) => {
    const { properties } = newElement;
    const { eventId } = properties || {};
    if (properties == null || eventId == null)
      throw new Error('Only can update geo elements with an id and properties!');

    const racemapType = properties.racemapType;
    const isCurrentEvent = eventId === get().events.currentEventId;

    await createGeoElementOfType(racemapType, newElement);

    if (isCurrentEvent && reload) {
      await get().events.actions.loadCurrentEvent(eventId);
    }
  },
  deleteGeoElement: async (element) => {
    const { id, properties } = element;
    const { eventId } = properties || {};
    if (id == null || properties == null || eventId == null)
      throw new Error('Only can update geo elements with an id and properties!');

    const { isCurrentEvent, rollBack } = removeFeaturesOptimistically(
      set,
      get,
      eventId,
      (f) => f.id === id,
    );

    try {
      await removeGeoElementOfType(id, properties.racemapType);
    } catch (error) {
      console.log(error);
      toast.error('Failed to remove the track from the map.');
      rollBack();
    }

    if (isCurrentEvent) {
      await get().events.actions.loadCurrentEvent(eventId);
    }
  },
  deleteGeoElements: async (elements) => {
    if (elements.length === 0) return;

    const eventId = elements[0].properties?.eventId;
    if (eventId == null) throw new Error('Only can delete geo elements with an eventId!');

    const validElements = elements.filter(
      (element) => element.id != null && element.properties?.eventId === eventId,
    );
    if (validElements.length === 0) return;

    const idsToRemove = new Set(validElements.map((element) => element.id));
    const { isCurrentEvent, rollBack } = removeFeaturesOptimistically(set, get, eventId, (f) =>
      idsToRemove.has(f.id),
    );

    try {
      const results = await Promise.allSettled(
        validElements.map((element) =>
          removeGeoElementOfType(element.id, element.properties.racemapType),
        ),
      );

      if (results.some((result) => result.status === 'rejected')) {
        throw new Error('Failed to remove one or more geo elements.');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to remove the elements from the map.');
      rollBack();
    }

    if (isCurrentEvent) {
      await get().events.actions.loadCurrentEvent(eventId);
    }
  },
  changeShadowtrack: async (eventId, shadowtrackId) => {
    const event = get().events.items.get(eventId);
    const isCurrentEvent = eventId === get().events.currentEventId;
    if (event == null) return;

    if (event.geo == null) throw new Error('Only can update the shadowtrack of the current event.');

    get().events.actions.updateEvent(eventId, [
      {
        key: 'geo',
        newValue: { ...event.geo, shadowtrackId },
      },
    ]);

    if (isCurrentEvent) {
      await get().events.actions.loadCurrentEvent(eventId);
    }
  },
});
